import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, CheckCircle2, Circle, Clock, Trash2, Edit3, X,
  ArrowRight, AlertCircle, Calendar,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_STYLE = {
  low: 'bg-slate-100 text-slate-600',
  normal: 'bg-blue-100 text-blue-700',
  high: 'bg-red-100 text-red-700',
};

const STATUS_STYLE = {
  open: { icon: Circle, color: 'text-slate-400', label: 'Open' },
  in_progress: { icon: Clock, color: 'text-blue-500', label: 'In Progress' },
  complete: { icon: CheckCircle2, color: 'text-emerald-500', label: 'Complete' },
};

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

// ── Task Creation Modal ──
export function TaskModal({ open, onClose, agents, leads, prefillAgent, onCreated }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({ title: '', description: '', assigned_to: '', related_lead: '', due_date: '', priority: 'normal' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (prefillAgent) setForm(f => ({ ...f, assigned_to: prefillAgent.id }));
  }, [prefillAgent]);

  useEffect(() => {
    if (open) setForm({ title: '', description: '', assigned_to: prefillAgent?.id || '', related_lead: '', due_date: '', priority: 'normal' });
  }, [open, prefillAgent]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!form.title || !form.assigned_to) {
      toast.error('Title and agent are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/tasks`, form, getAuthHeaders());
      toast.success('Task assigned');
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="task-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-[#111]">Assign task</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Task title *</label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g., Follow up with Dr. Smith" data-testid="task-title-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Description</label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional details..." rows={2} data-testid="task-description-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Assigned to *</label>
            <Select value={form.assigned_to} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
              <SelectTrigger data-testid="task-agent-select"><SelectValue placeholder="Select agent" /></SelectTrigger>
              <SelectContent>
                {(agents || []).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Related lead</label>
            <Select value={form.related_lead} onValueChange={v => setForm(f => ({ ...f, related_lead: v }))}>
              <SelectTrigger data-testid="task-lead-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {(leads || []).map(l => <SelectItem key={l.id} value={l.id}>{l.organization_name || l.contact_name || l.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Due date</label>
              <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="task-due-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Priority</label>
              <div className="flex bg-[#f0f0f0] rounded-lg p-0.5">
                {['low', 'normal', 'high'].map(p => (
                  <button key={p} onClick={() => setForm(f => ({ ...f, priority: p }))}
                    className={`flex-1 text-[11px] py-1.5 rounded-md transition-all capitalize ${form.priority === p ? 'bg-white text-[#111] font-semibold shadow-sm' : 'text-slate-500'}`}
                    data-testid={`task-priority-${p}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-[#111] hover:bg-[#333]" onClick={handleSubmit} disabled={submitting} data-testid="task-submit-btn">
            {submitting ? 'Assigning...' : 'Assign task'}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ── Admin Tasks Page ──
export default function AdminTasks() {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('mine');
  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const loadTasks = async () => {
    try {
      const [taskRes, agentRes, leadRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/tasks?tab=${tab}`, getAuthHeaders()),
        axios.get(`${API_URL}/api/admin/agents`, getAuthHeaders()),
        axios.get(`${API_URL}/api/leads`, getAuthHeaders()),
      ]);
      setTasks(taskRes.data.tasks || []);
      setAgents(agentRes.data.agents || []);
      setLeads(leadRes.data.leads || leadRes.data || []);
    } catch (e) {
      console.error('Load tasks error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTasks(); }, [tab]);

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}`, { status }, getAuthHeaders());
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status } : t));
      toast.success('Task updated');
    } catch (e) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, getAuthHeaders());
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch (e) {
      toast.error('Failed to delete task');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#111]">Tasks</h1>
          <p className="text-sm text-slate-500">Manage agent task assignments</p>
        </div>
        <Button size="sm" className="text-xs gap-1.5 bg-[#111] hover:bg-[#333]" onClick={() => setShowModal(true)} data-testid="new-task-btn">
          <Plus className="w-3.5 h-3.5" /> New task
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex bg-[#f0f0f0] rounded-lg p-[3px] w-fit">
        {[{ id: 'mine', label: 'Assigned by me' }, { id: 'all', label: 'All tasks' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`text-xs py-[5px] px-[14px] rounded-md transition-all ${tab === t.id ? 'bg-white text-[#111] font-semibold shadow-sm' : 'text-[#888]'}`}
            data-testid={`tasks-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Task List */}
      <Card className="border border-[#ebebeb] shadow-none rounded-[10px]">
        <CardContent className="p-0">
          {loading ? (
            <div className="px-5 py-8 text-center text-slate-400 text-xs">Loading...</div>
          ) : tasks.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No tasks yet</p>
              <Button variant="outline" size="sm" className="mt-3 text-xs" onClick={() => setShowModal(true)}>Create first task</Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {tasks.map((task) => {
                const St = STATUS_STYLE[task.status] || STATUS_STYLE.open;
                return (
                  <div key={task.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/50 transition-colors" data-testid={`task-row-${task.id}`}>
                    <button onClick={() => updateTaskStatus(task.id, task.status === 'complete' ? 'open' : task.status === 'open' ? 'in_progress' : 'complete')}>
                      <St.icon className={`w-4 h-4 ${St.color}`} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${task.status === 'complete' ? 'text-slate-400 line-through' : 'text-[#111]'} truncate`}>{task.title}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-slate-400">Assigned to: {task.assigned_to_name}</span>
                        {task.related_lead_name && <span className="text-[10px] text-slate-400">| Lead: {task.related_lead_name}</span>}
                        {task.due_date && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{formatDate(task.due_date)}</span>}
                      </div>
                    </div>
                    <Badge className={`text-[9px] py-0 border-0 ${PRIORITY_STYLE[task.priority] || ''}`}>{task.priority}</Badge>
                    <Badge className={`text-[9px] py-0 border-0 ${
                      task.status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{St.label}</Badge>
                    <button onClick={() => deleteTask(task.id)} className="p-1 hover:bg-red-50 rounded text-slate-300 hover:text-red-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <TaskModal
        open={showModal}
        onClose={() => setShowModal(false)}
        agents={agents}
        leads={leads}
        onCreated={loadTasks}
      />
    </div>
  );
}
